import { Box, Button, Tooltip, Typography } from '@mui/material';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { AskRoundSettingsResponse } from 'generated/index';
import { MouseEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { defaultRoundValues } from '../../AIMatch';
import schema from './schema';
import {
  ControlledAutoComplete,
  ControlledCheckbox
} from 'components/shared/ControlledFields';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import CustomDialog from 'components/shared/Dialog';

interface RoundModalProps {
  round?: { index: number; round: AskRoundSettingsResponse } | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (index: number, round: AskRoundSettingsResponse) => void;
  isEdit?: boolean;
}

const RoundModal = (props: RoundModalProps) => {
  const { isOpen, onClose, onSave, isEdit, round } = props;

  const { t } = useTranslation('common', { keyPrefix: 'aiMatchSettings' });

  const methods = useForm<AskRoundSettingsResponse>({
    defaultValues: defaultRoundValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const { control, getValues } = methods;

  const sortedFields = Object.entries(defaultRoundValues).sort(
    ([a], [b]) => t(`roundType.${a}`)?.localeCompare(t(`roundType.${b}`))
  );

  const saveRound = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    onSave(round!.index, {
      ...getValues(),
      responseActionType: (getValues().responseActionType as any)
        .value as AskRoundSettingsResponse['responseActionType']
    });
  };

  const getSettingByType = (type: keyof AskRoundSettingsResponse) => {
    switch (type) {
      case 'responseActionType':
        return (
          <Box key={type} display="flex" mt={2} gap={1}>
            <ControlledAutoComplete
              name={type}
              label={t(`roundType.${type}`)}
              control={control}
              optionLabelProp="label"
              idProperty="value"
              disableClearable
              options={[
                { label: t('connect'), value: 'CONNECT' },
                { label: t('subscribe'), value: 'SUBSCRIBE' }
              ]}
              variant="outlined"
            />
            <Tooltip title={t(`roundType.${type}Tooltip`)} arrow>
              <FontAwesomeSvgIcon icon={faInfoCircle} sx={{ mt: 1 }} />
            </Tooltip>
          </Box>
        );

      case 'minutesBeforeStart':
      case 'minimalMinutesOfAdvantageBeforeNext':
      case 'overlapMargin':
        return (
          <Box key={type} display="flex" mt={2} gap={1}>
            <ControlledNumericField
              name={type}
              label={t(`roundType.${type}`)}
              control={control}
            />
            <Tooltip title={t(`roundType.${type}Tooltip`)} arrow>
              <FontAwesomeSvgIcon icon={faInfoCircle} sx={{ mt: 1 }} />
            </Tooltip>
          </Box>
        );
      case 'includeOwn':
      case 'includeShared':
      case 'includePreference1':
      case 'includePreference2':
      case 'includeFlexWithPreference1':
      case 'includeFlexWithPreference2':
      case 'includeFlex':
      case 'includeDeployableAsFlexWithPreference1':
      case 'includeDeployableAsFlexWithPreference2':
      case 'includeDeployableAsFlex':
        return (
          <Box key={type} mt={1}>
            <ControlledCheckbox
              name={type}
              label={t(`roundType.${type}`)}
              control={control}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (round)
      methods.reset({
        ...round.round,
        responseActionType: {
          label: t(round.round.responseActionType.toLowerCase()),
          value: round.round.responseActionType
        } as any
      });
  }, [round]);

  return (
    <CustomDialog
      title={
        isEdit ? t('editRoundNr', { number: round!.index + 1 }) : t('addRound')
      }
      open={isOpen}
      onHidden={onClose}
      onClose={onClose}
      sx={{
        '& .MuiFormControl-root': {
          width: '500px'
        }
      }}
    >
      <Box>
        {sortedFields
          .filter(([, value]) => typeof value !== 'boolean')
          .map(([key]) =>
            getSettingByType(key as keyof AskRoundSettingsResponse)
          )}
      </Box>

      <Box>
        {sortedFields
          .filter(
            ([key, value]) => !key.match(/flex/i) && typeof value === 'boolean'
          )
          .map(([key]) =>
            getSettingByType(key as keyof AskRoundSettingsResponse)
          )}
      </Box>

      <Box mt={2}>
        <Typography mb={2}>{t('flex')}</Typography>
        {sortedFields
          .filter(
            ([key, value]) => key.match(/flex/i) && typeof value === 'boolean'
          )
          .sort(([a], [b]) => {
            return t(`roundType.${a}`)?.localeCompare(t(`roundType.${b}`));
          })
          .map(([key]) =>
            getSettingByType(key as keyof AskRoundSettingsResponse)
          )}
      </Box>

      <Button
        type="submit"
        variant="contained"
        color="dark"
        sx={{
          mt: 2,
          float: 'right'
        }}
        onClick={saveRound}
      >
        {t('save')}
      </Button>
    </CustomDialog>
  );
};

export default RoundModal;
